import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Form, Button, Alert, Spinner } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import {
  checkLifeRenewalEligibility,
  checkAssociateRenewalEligibility,
} from "../utils/razorpay";

const MembershipRenewalUpload = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [certificateFile, setCertificateFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [eligibilityError, setEligibilityError] = useState("");
  const [renewalInfo, setRenewalInfo] = useState(null);

  const baseUrl = process.env.REACT_APP_BASEURL;

  // 🔹 Restore uploaded files when coming back from terms page
  useEffect(() => {
    if (location.state?.certificateFile) {
      setCertificateFile(location.state.certificateFile);
    }
    if (location.state?.photoFile) {
      setPhotoFile(location.state.photoFile);
    }
  }, [location.state]);

  // Check renewal eligibility on component mount
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        setLoading(true);
        setEligibilityError("");

        const user = JSON.parse(localStorage.getItem("user"));
        const membershipType =
          user?.rolename === "LifeMember" ? "life" : "associate";
        const membershipId = user?.details_id;

        if (!membershipId) {
          setEligibilityError(
            "Unable to find membership ID. Please login again."
          );
          return;
        }

        let eligibilityResponse;
        if (membershipType === "life") {
          eligibilityResponse = await checkLifeRenewalEligibility({
            baseUrl,
            membershipId,
          });
        } else {
          eligibilityResponse = await checkAssociateRenewalEligibility({
            baseUrl,
            membershipId,
          });
        }

        console.log("Eligibility response:", eligibilityResponse);

        if (eligibilityResponse.success) {
          const renewalData = eligibilityResponse.data;

          setRenewalInfo({
            ...renewalData,
            currentExpiryDate: renewalData.valid_till,
            // Calculate new expiry date (1 year from current expiry)
            newExpiryDate: new Date(
              new Date(renewalData.valid_till).getFullYear() + 1,
              new Date(renewalData.valid_till).getMonth(),
              new Date(renewalData.valid_till).getDate()
            ),
            renewalFee: renewalData.renewal_amount,
            canRenew: renewalData.can_renew,
            daysUntilExpiration: renewalData.days_until_expiration,
            message: renewalData.renewal_message,
            // Include late fee information from member data if available
            amount_IN_renewal: renewalData.amount_IN_renewal || location.state?.memberData?.amount_IN_renewal,
            renewal_late_fee: renewalData.renewal_late_fee || location.state?.memberData?.renewal_late_fee,
            valid_till: renewalData.valid_till || location.state?.memberData?.validTill,
          });
          setEligibilityChecked(true);
        } else {
          setEligibilityError(
            eligibilityResponse.message || "Unable to check renewal eligibility"
          );
        }
      } catch (error) {
        console.error("Eligibility check error:", error);
        setEligibilityError(
          error.message || "Failed to check renewal eligibility"
        );
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [baseUrl]);

  useEffect(() => {
    if (renewalInfo) {
      console.log("✅ Renewal Info:", renewalInfo);
    }
  }, [renewalInfo]);

  const handleProceed = async () => {
    if (!certificateFile || !photoFile) {
      setError(
        "Please upload both your membership certificate and passport-size photo."
      );
      return;
    }
    console.log("Uploading state...", location.state);
    setError("");
    setLoading(true);
    let memberId = location.state?.memberData?._id;
    if (!memberId || memberId === "undefined") {
      try {
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user"));
        const decoded = jwtDecode(token);
        console.log("Decoded token:", user);
        if (!token) {
          setError("No authentication token found. Please login again.");
          return;
        }
        let response = null;
        if (user.rolename === "LifeMember") {
          response = await fetch(
            `${process.env.REACT_APP_BASEURL}/getLifemembershipById/${user.details_id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        }
        if (user.rolename === "AssociateMember") {
          response = await fetch(
            `${process.env.REACT_APP_BASEURL}/associate-membership/${user.details_id}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Response text:", errorText);
          setError("Failed to fetch membership details. Please login again.");
          return;
        }
        const result = await response.json();
        if (!result.success) {
          setError(
            result.message ||
              "Failed to fetch membership details. Please login again."
          );
          return;
        }
        memberId = result.data._id;
        if (!memberId || memberId === "undefined") {
          setError(
            "No life membership found for your account. Please contact support."
          );
          return;
        }
      } catch (err) {
        console.error("Error fetching membership:", err);
        setError("Error fetching membership details. Please try again.");
        return;
      }
    }
    if (!memberId || memberId === "undefined") {
      setError("Invalid member ID. Please login again.");
      return;
    }
    const formData = new FormData();
    formData.append("mem_renewal_certificate_upload", certificateFile);
    formData.append("mem_renewal_profile_photo", photoFile);
    
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      
      const endpoint = user?.rolename === "AssociateMember" 
        ? `${process.env.REACT_APP_BASEURL}/associate/upload-renewal-docs/${memberId}`
        : `${process.env.REACT_APP_BASEURL}/upload-renewal-docs/${memberId}`;
      
      console.log("Uploading to endpoint:", endpoint);
      console.log("User role:", user?.rolename);
      
      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload response text:", errorText);
        try {
          const errorResult = JSON.parse(errorText);
          setError(
            errorResult.message || "Failed to upload documents. Please try again."
          );
        } catch (parseError) {
          setError("Failed to upload documents. Please try again.");
        }
        return;
      }
      
      const result = await response.json();
      console.log("Upload response:", result);
      
      if (!result.success) {
        setError(
          result.message || "Failed to upload documents. Please try again."
        );
        return;
      }
      
      // 🔴 CRITICAL CHANGE: Check if this is a resubmission (secretary rejected within same cycle)
      // Backend will return isResubmission: true if it's a resubmission after secretary rejection
      if (result.isResubmission) {
        console.log("Resubmission after secretary rejection - skipping payment page");
        
        // Show custom success message
        const alertDiv = document.createElement('div');
        alertDiv.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          padding: 30px 40px;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
          z-index: 10000;
          max-width: 500px;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        `;

        alertDiv.innerHTML = `
          <h3 style="margin: 0 0 15px 0; color: #28a745; font-size: 20px; font-weight: 600;">
            Documents Uploaded Successfully!
          </h3>
          <div style="margin: 0 0 25px 0; color: #666; font-size: 16px; line-height: 1.5; text-align: left;">
            <p style="margin: 0 0 10px 0;">
              ✓ Your renewal documents have been updated for re-review.
            </p>
            <p style="margin: 0 0 10px 0;">
              ✓ No payment required - your previous payment is still valid.
            </p>
            <p style="margin: 0;">
              ✓ Secretary will review your updated documents.
            </p>
          </div>
          <button onclick="this.parentElement.remove(); document.getElementById('overlay-backdrop').remove();" 
            style="background: #007bff; color: white; border: none; padding: 10px 30px; 
            border-radius: 6px; font-size: 16px; cursor: pointer; font-weight: 500;">
            OK
          </button>
        `;

        const backdrop = document.createElement('div');
        backdrop.id = 'overlay-backdrop';
        backdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.5);
          z-index: 9999;
        `;
        backdrop.onclick = () => {
          alertDiv.remove();
          backdrop.remove();
        };

        document.body.appendChild(backdrop);
        document.body.appendChild(alertDiv);
        
        // Navigate back to profile
        setTimeout(() => {
          navigate("/aiota-member-profile");
        }, 3000);
      } else {
        // This is a NEW renewal process - go to payment page
        console.log("New renewal process - redirecting to payment page");
        
        // Navigate to terms and payment page
        navigate("/membership-renewal-terms", {
          state: {
            ...location.state,
            memberData: { ...location.state?.memberData, _id: memberId },
            certificateFile,
            photoFile,
            uploadedDocs: result.data,
            renewalInfo: result.data || renewalInfo,
          },
        });
      }
    } catch (err) {
      console.error("Error uploading documents:", err);
      setError("Error uploading documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while checking eligibility
  if (loading) {
    return (
      <Container className='py-5 text-center'>
        <Spinner animation='border' className='me-2' />
        <span>Checking renewal eligibility...</span>
      </Container>
    );
  }

  // Show eligibility error if renewal is not allowed
  if (eligibilityError) {
    return (
      <Container className='py-5'>
        <h2 className='mb-4'>Membership Renewal - Error</h2>
        <Alert variant='danger'>
          <strong>Error:</strong> {eligibilityError}
        </Alert>
        <Button variant='outline-secondary' onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Container>
    );
  }

  // Show renewal status message
  if (eligibilityChecked && renewalInfo) {
    if (!renewalInfo.can_renew) {
      return (
        <Container className='py-5'>
          <h2 className='mb-4'>Membership Renewal - Not Yet Available</h2>
          {renewalInfo?.is_treasurer_renewal_approved === "approved" &&
            renewalInfo?.is_secreatry_renewal_approved === "approved" &&
            renewalInfo?.is_certificate_id_card_issued_renewal && (
              <Alert variant='info'>
                <h5>Current Membership Status</h5>
                <p>
                  Valid until:{" "}
                  {new Date(renewalInfo.currentExpiryDate).toLocaleDateString()}
                </p>
                <p>
                  Days until expiration: {renewalInfo.daysUntilExpiration} days
                </p>
                <p>
                  <strong>Note:</strong> Renewal will be available 30 days
                  before expiration.
                </p>
              </Alert>
            )}

          <Button
            variant='outline-secondary'
            className='mt-3'
            onClick={() => navigate(-1)}
          >
            Go Back
          </Button>
        </Container>
      );
    }
  }

  return (
    <Container className='py-5'>
      <h2 className='mb-4'>Membership Renewal - Upload Documents</h2>

      {/* Show renewal info only after approvals done and certificate issued; otherwise show waiting message when payment is done */}
      {renewalInfo && (
        <>
          {(renewalInfo.is_treasurer_renewal_approved === "approved" ||
            renewalInfo.treasurer_approval_status === "approved") &&
          (renewalInfo.is_secretary_renewal_approved === "approved" ||
            renewalInfo.is_secreatry_renewal_approved === "approved" ||
            renewalInfo.secretary_renewal_approval_status === "approved") &&
          renewalInfo.is_certificate_id_card_issued_renewal === true ? (
            <Alert variant='info' className='mb-4'>
              <h5>Renewal Information</h5>
            <p>
  Current expiry date:{" "}
  {new Date(renewalInfo.currentExpiryDate).toLocaleDateString('en-GB')}
</p>
<p>
  Days until expiration: {
    (() => {
      const currentDate = new Date();
      const expiry = new Date(renewalInfo.currentExpiryDate);
      currentDate.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      const timeDiff = expiry.getTime() - currentDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      if (daysDiff < 0) return "Expired";
      if (daysDiff === 0) return "Expires today";
      return `${daysDiff} days`;
    })()
  }
</p>
<p>
  Renewal fee: ₹{
    (() => {
      const currentDate = new Date();
      const expiry = new Date(renewalInfo.currentExpiryDate);
      currentDate.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);
      const timeDiff = expiry.getTime() - currentDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      if (daysDiff < 0) {
        return renewalInfo.amount_IN_renewal + (renewalInfo.renewal_late_fee || 0);
      } else {
        return renewalInfo.amount_IN_renewal;
      }
    })()
  }
</p>
              <p>
                <strong>{renewalInfo.message}</strong>
              </p>
            </Alert>
          ) : (
    renewalInfo.renewal_payment_status === "paid" ? (
      <Alert variant='info' className='mb-4'>
        <strong>Payment Completed:</strong>{" "}
        {renewalInfo.is_treasurer_renewal_approved === "approved" && 
         renewalInfo.is_secretary_renewal_approved === "rejected" 
          ? "Waiting for Secretary approval."
          : "Waiting for Treasurer and Secretary approvals."}
      </Alert>
    ) : null
  )}
          {/* Renewal Process guidance when payment not done and approvals not completed */}
          {(!renewalInfo.renewal_payment_status ||
            renewalInfo.renewal_payment_status !== "paid") && (
            <Alert variant='info' className='mb-4'>
              <strong>Renewal Process:</strong>
              <br />
              • Renewal is allowed only 30 days before your membership expires
              <br />
              • You will be required to upload your membership certificate and a
              passport-size photo
              <br />
              • You must accept the terms and conditions before payment
              <br />• Life memberships are renewed for 15 years, Associate
              memberships for 1 year
            </Alert>
          )}
        </>
      )}

      <Alert variant='info'>
        Please upload your AIOTA Membership certificate and a passport-size
        photograph. <br />
        <strong>Note:</strong> Once uploaded and you proceed, these files cannot
        be edited. <br />
        If your membership certificate has been lost, you must submit a
        declaration letter addressed to the Honorary Secretary, along with your
        membership number.
      </Alert>
      {error && <Alert variant='danger'>{error}</Alert>}
      <Form>
        <Form.Group className='mb-3'>
          <Form.Label>Membership Certificate (PDF/JPG/PNG)</Form.Label>
          <div>
            <Button
              variant='secondary'
              onClick={() =>
                document.getElementById("certificateUpload").click()
              }
            >
              Choose File
            </Button>
            <span className='ms-2'>
              {certificateFile ? certificateFile.name : "No file chosen"}
            </span>
            <Form.Control
              id='certificateUpload'
              type='file'
              accept='.pdf,.jpg,.jpeg,.png'
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && file.size > 10 * 1024 * 1024) {
                  setError("Membership Certificate size exceeds 10MB.");
                  e.target.value = "";
                  setCertificateFile(null);
                  return;
                }
                setCertificateFile(file);
              }}
              style={{ display: "none" }}
            />
          </div>
        </Form.Group>

        <Form.Group className='mb-3'>
          <Form.Label>Passport-size Photo (JPG/PNG)</Form.Label>
          <div>
            <Button
              variant='secondary'
              onClick={() => document.getElementById("photoUpload").click()}
            >
              Choose File
            </Button>
            <span className='ms-2'>
              {photoFile ? photoFile.name : "No file chosen"}
            </span>
            <Form.Control
              id='photoUpload'
              type='file'
              accept='.jpg,.jpeg,.png'
              onChange={(e) => {
                const file = e.target.files[0];
                if (file && file.size > 10 * 1024 * 1024) {
                  setError("Passport-size Photo size exceeds 10MB.");
                  e.target.value = "";
                  setPhotoFile(null);
                  return;
                }
                setPhotoFile(file);
              }}
              style={{ display: "none" }}
            />
          </div>
        </Form.Group>

        <Button variant='primary' onClick={handleProceed}>
          Proceed
        </Button>
      </Form>
    </Container>
  );
};

export default MembershipRenewalUpload;